import { useQueries, useQuery } from "@tanstack/react-query";
import { LngLat } from "maplibre-gl";

import { RasterPoint } from "@nens/lizard/api-v4";
import { getRanaApiV1 } from "@nens/rana/api-v1";
import { convertPointToGeometry } from "@nens/react-map-gl/utils";
import { combineUrlAndParams, pointToWkt } from "@nens/shared-utils";

import { getSearchStatePublicationId } from "./utils/get-search-state-publication-id";

// Single (lizard raster point data)

const createFileDescriptorApiUrl = (
  tenantId: string,
  descriptorId: string,
  endpoint: "lizard-raster-point" | "lizard-scenario-point",
  publicationId?: string
) => {
  const basePath = publicationId
    ? `/tenants/${tenantId}/publications/${publicationId}/file-descriptors/${descriptorId}`
    : `/tenants/${tenantId}/file-descriptors/${descriptorId}`;

  return `${basePath}/${endpoint}`;
};

const getLizardRasterPointDataQuery = (descriptorId: string, point?: LngLat | null) => {
  const { BaseApi, tenantId, getQueryOptions } = getRanaApiV1();
  return getQueryOptions<RasterPoint | null>({
    queryKey: ["lizard-raster-point-data", descriptorId, point],
    queryFn: async () => {
      if (!point || !tenantId) return null;
      const pointGeometry = convertPointToGeometry(point);
      const geom = pointToWkt(pointGeometry);

      const publicationId = getSearchStatePublicationId();

      const baseUrl = createFileDescriptorApiUrl(
        tenantId,
        descriptorId,
        "lizard-raster-point",
        publicationId ?? undefined
      );
      const url = combineUrlAndParams(baseUrl, { geom });
      const response = await BaseApi.fetch(url);
      return await response.json();
    },
    refetchInterval: (query) => (query.state.data ? false : 2000),
    enabled: !!point,
  });
};

export const useLizardRasterPointDataQuery = (descriptorId: string, point?: LngLat | null) =>
  useQuery(getLizardRasterPointDataQuery(descriptorId, point));

// Single (scenario point data)

const getLizardScenarioPointDataQuery = (
  descriptorId: string,
  layerId: string | null,
  point?: LngLat | null
) => {
  const { BaseApi, tenantId, getQueryOptions } = getRanaApiV1();
  return getQueryOptions<RasterPoint | null>({
    queryKey: ["lizard-scenario-point-data", descriptorId, layerId, point],
    queryFn: async () => {
      if (!point || !layerId || !tenantId) return null;
      const publicationId = getSearchStatePublicationId();
      const pointGeometry = convertPointToGeometry(point);
      const geom = pointToWkt(pointGeometry);

      const baseUrl = createFileDescriptorApiUrl(
        tenantId,
        descriptorId,
        "lizard-scenario-point",
        publicationId ?? undefined
      );
      const url = combineUrlAndParams(baseUrl, { geom, layer_id: layerId });
      const response = await BaseApi.fetch(url);
      return await response.json();
    },
    refetchInterval: (query) => (query.state.data ? false : 2000),
    enabled: !!point && !!layerId,
  });
};

export const useLizardScenarioPointDataQuery = (
  descriptorId: string,
  layerIds: (string | null)[],
  point?: LngLat | null
) =>
  useQueries({
    queries: layerIds.map((layerId) =>
      getLizardScenarioPointDataQuery(descriptorId, layerId, point)
    ),
  });
